import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';

import { LiveRegion } from './LiveRegion';

/**
 * Placeholders the shape of a page that is still loading.
 *
 * A page that showed one line ("Loading your account…") where its form would
 * be collapsed to nothing and then jumped when the form arrived. These hold
 * the space instead. The sentence stays, read by a screen reader and not
 * shown, so what is announced does not change. MUI hides the shapes
 * themselves from assistive technology.
 *
 * A page's text wraps more on a phone than on a desktop, so the real page is
 * taller there. Each placeholder can take a `minHeight` per screen width,
 * measured from the page it stands in for, so it matches at both.
 */

/** A height in pixels, or one per breakpoint: `{ xs: 158, md: 138 }`. */
export type SkeletonHeight =
  number | { xs?: number; sm?: number; md?: number; lg?: number };

interface FieldsProps {
  fields: number;
  columns: 1 | 2;
}

/** Label-and-box pairs, laid out as the forms lay out their fields. */
function Fields({ fields, columns }: FieldsProps) {
  return (
    <Box
      data-testid="form-skeleton"
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: columns === 2 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
        },
      }}
    >
      {Array.from({ length: fields }, (_, index) => (
        <Box key={index} data-testid="form-skeleton-field">
          <Skeleton width="35%" sx={{ mb: 0.5 }} />
          {/* The height of a small text field. */}
          <Skeleton variant="rounded" height={40} />
        </Box>
      ))}
    </Box>
  );
}

interface FormSkeletonProps {
  /** How many label-and-box pairs the form has. */
  fields?: number;
  /** Two puts the fields side by side at desktop width, as the wider forms do. */
  columns?: 1 | 2;
  /** Said once to a screen reader. */
  label?: string;
}

/** A form's fields and its button, for a page that is a single form. */
export function FormSkeleton({
  fields = 6,
  columns = 1,
  label = 'Loading',
}: FormSkeletonProps) {
  return (
    <Box sx={{ maxWidth: columns === 2 ? 760 : 560 }}>
      <LiveRegion message={label} />
      <Fields fields={fields} columns={columns} />
      <Skeleton variant="rounded" width={120} height={36} sx={{ mt: 3 }} />
    </Box>
  );
}

interface SectionSkeletonProps {
  /** Fields drawn inside. */
  fields?: number;
  columns?: 1 | 2;
  /** The section's height once loaded, so the page does not jump. */
  minHeight?: SkeletonHeight;
}

/**
 * One outlined section of a longer form - a heading and its fields - as the
 * Profile and staff account pages divide theirs. Says nothing to a screen
 * reader: the page announces its loading once, elsewhere.
 */
export function SectionSkeleton({
  fields = 2,
  columns = 1,
  minHeight,
}: SectionSkeletonProps) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 3, minHeight, overflow: 'hidden' }}
      data-testid="section-skeleton"
    >
      <Skeleton width="30%" sx={{ mb: 2 }} />
      <Fields fields={fields} columns={columns} />
    </Paper>
  );
}

interface CardSkeletonProps {
  /** Lines of text under the heading. */
  lines?: number;
  /**
   * Said once to a screen reader. Left out when the page announces its
   * loading elsewhere, so the person hears it once, not once per card.
   */
  label?: string;
  /** The card's height once loaded, so the page does not jump. */
  minHeight?: SkeletonHeight;
}

export function CardSkeleton({ lines = 3, label, minHeight }: CardSkeletonProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minHeight }} data-testid="card-skeleton">
      {label ? <LiveRegion message={label} /> : null}
      <Skeleton width="50%" height={28} />
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} width={index === lines - 1 ? '60%' : '90%'} />
      ))}
    </Paper>
  );
}
